/**
 * 软件光栅化器：把低模网格画成带透明通道的等距精灵。
 *
 * 投影必须与 `iso.ts` 的 tileToWorld 同构，否则精灵正面会相对移动方向偏 90°/180°
 * （螃蟹走、倒退走）。不要用「绕 Y 转 45° 的 3D 相机」——那套的屏幕 XY 和游戏是对调的。
 *
 *   sx = (x - z) * scale                       // 同 tileToWorldX
 *   sy = (x + z) * scale * 0.5 - y * scale     // 同 tileToWorldY，高度沿屏幕向上
 *
 * 只在启动时跑一次，运行期 Pixi 只 blit 烘焙好的纹理。
 */

import { FILL_LIGHT, KEY_LIGHT, facingY, xfDir, xfPoint, type Vec3 } from './math.ts';
import type { Mesh } from './mesh.ts';

export interface BakeOpts {
  /** 1 格对应的像素半宽（与 HALF_W 同量纲时投影比例正确） */
  scale: number;
  /** 模型绕 Y 的偏航（格空间）：0 = +Y 屏幕左下，π/2 = +X 屏幕右下 */
  yaw?: number;
  pad?: number;
  grain?: number;
  shadow?: boolean;
}

export interface RasterResult {
  canvas: HTMLCanvasElement;
  /** 地面原点 (0,0,0) 在画布中的像素坐标 */
  groundX: number;
  groundY: number;
}

interface PVert {
  sx: number;
  sy: number;
  z: number;
  r: number;
  g: number;
  b: number;
}

/** x=格X, y=高度, z=格Y。屏幕下方（东南）更近，depth 越小越近。 */
function projectIso(x: number, y: number, z: number, scale: number): { sx: number; sy: number; z: number } {
  return {
    sx: (x - z) * scale,
    sy: (x + z) * scale * 0.5 - y * scale,
    z: -(x + z) - y * 1.6,
  };
}

function shadeVert(n: Vec3, cr: number, cg: number, cb: number): [number, number, number] {
  const kd = Math.max(0, n.x * KEY_LIGHT.x + n.y * KEY_LIGHT.y + n.z * KEY_LIGHT.z);
  const kf = Math.max(0, n.x * FILL_LIGHT.x + n.y * FILL_LIGHT.y + n.z * FILL_LIGHT.z);
  const lit = 0.28 + kd * 0.72 + kf * 0.22;
  const sky = 0.08 * Math.max(0, n.y);
  const m = lit + sky;
  return [Math.min(1, cr * m), Math.min(1, cg * m), Math.min(1, cb * m)];
}

function hash2(x: number, y: number): number {
  let n = (x * 374761393 + y * 668265263) | 0;
  n = (n ^ (n >>> 13)) * 1274126177;
  return ((n ^ (n >>> 16)) >>> 0) / 4294967295;
}

export function rasterize(mesh: Mesh, opts: BakeOpts): RasterResult {
  const scale = opts.scale;
  const facing = facingY(opts.yaw ?? 0);
  const n = mesh.vertCount;
  const cam: Array<{ x: number; y: number; z: number; r: number; g: number; b: number }> = new Array(n);

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (let i = 0; i < n; i++) {
    const w = xfPoint(facing, mesh.px[i]!, mesh.py[i]!, mesh.pz[i]!);
    const worldN = xfDir(facing, mesh.nx[i]!, mesh.ny[i]!, mesh.nz[i]!);
    const [r, g, b] = shadeVert(worldN, mesh.cr[i]!, mesh.cg[i]!, mesh.cb[i]!);
    const p = projectIso(w.x, w.y, w.z, scale);
    cam[i] = { x: p.sx, y: p.sy, z: p.z, r, g, b };
    if (p.sx < minX) minX = p.sx;
    if (p.sy < minY) minY = p.sy;
    if (p.sx > maxX) maxX = p.sx;
    if (p.sy > maxY) maxY = p.sy;
  }

  const gp = projectIso(0, 0, 0, scale);
  const gx = gp.sx;
  const gy = gp.sy;
  if (gx < minX) minX = gx;
  if (gy < minY) minY = gy;
  if (gx > maxX) maxX = gx;
  if (gy > maxY) maxY = gy;

  const pad = opts.pad ?? 6;
  minY -= 2;
  maxY += 8;
  minX -= 4;
  maxX += 4;

  const w = Math.max(8, Math.ceil(maxX - minX) + pad * 2);
  const h = Math.max(8, Math.ceil(maxY - minY) + pad * 2);
  const ox = -minX + pad;
  const oy = -minY + pad;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(w, h);
  const data = img.data;
  const zbuf = new Float32Array(w * h);
  zbuf.fill(1e9);

  const groundX = gx + ox;
  const groundY = gy + oy;

  if (opts.shadow !== false) {
    stampShadow(data, w, h, mesh, facing, scale, ox, oy, groundX, groundY);
  }

  const idx = mesh.idx;
  for (let t = 0; t < idx.length; t += 3) {
    const a = cam[idx[t]!]!;
    const b = cam[idx[t + 1]!]!;
    const c = cam[idx[t + 2]!]!;
    fillTri(
      data,
      zbuf,
      w,
      h,
      { sx: a.x + ox, sy: a.y + oy, z: a.z, r: a.r, g: a.g, b: a.b },
      { sx: b.x + ox, sy: b.y + oy, z: b.z, r: b.r, g: b.g, b: b.b },
      { sx: c.x + ox, sy: c.y + oy, z: c.z, r: c.r, g: c.g, b: c.b },
      opts.grain ?? 0.07,
    );
  }

  ctx.putImageData(img, 0, 0);
  return { canvas, groundX, groundY };
}

function stampShadow(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  mesh: Mesh,
  facing: ReturnType<typeof facingY>,
  scale: number,
  ox: number,
  oy: number,
  gx: number,
  gy: number,
): void {
  let minX = gx;
  let maxX = gx;
  let minY = gy;
  let maxY = gy;
  for (let i = 0; i < mesh.vertCount; i++) {
    const wp = xfPoint(facing, mesh.px[i]!, 0, mesh.pz[i]!);
    const p = projectIso(wp.x, 0, wp.z, scale);
    const sx = p.sx + ox;
    const sy = p.sy + oy + 2;
    if (sx < minX) minX = sx;
    if (sx > maxX) maxX = sx;
    if (sy < minY) minY = sy;
    if (sy > maxY) maxY = sy;
  }
  const rx = Math.max(4, (maxX - minX) * 0.52);
  const ry = Math.max(2, (maxY - minY) * 0.38);
  const cx = (minX + maxX) * 0.5;
  const cy = gy + 3;
  const x0 = Math.max(0, (cx - rx) | 0);
  const x1 = Math.min(w - 1, (cx + rx) | 0);
  const y0 = Math.max(0, (cy - ry) | 0);
  const y1 = Math.min(h - 1, (cy + ry) | 0);
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const dx = (x - cx) / rx;
      const dy = (y - cy) / ry;
      const d = dx * dx + dy * dy;
      if (d > 1) continue;
      const a = (0.34 * (1 - d) * (1 - d)) | 0;
      const o = (y * w + x) * 4;
      if (a > data[o + 3]!) {
        data[o] = 8;
        data[o + 1] = 6;
        data[o + 2] = 4;
        data[o + 3] = Math.min(90, a * 255);
      }
    }
  }
}

function fillTri(
  data: Uint8ClampedArray,
  zbuf: Float32Array,
  w: number,
  h: number,
  a: PVert,
  b: PVert,
  c: PVert,
  grain: number,
): void {
  let area = (b.sx - a.sx) * (c.sy - a.sy) - (b.sy - a.sy) * (c.sx - a.sx);
  if (area < 0) {
    const tmp = b;
    b = c;
    c = tmp;
    area = -area;
  }
  if (area <= 0.4) return;

  const minX = Math.max(0, Math.floor(Math.min(a.sx, b.sx, c.sx)));
  const maxX = Math.min(w - 1, Math.ceil(Math.max(a.sx, b.sx, c.sx)));
  const minY = Math.max(0, Math.floor(Math.min(a.sy, b.sy, c.sy)));
  const maxY = Math.min(h - 1, Math.ceil(Math.max(a.sy, b.sy, c.sy)));
  if (minX > maxX || minY > maxY) return;

  const inv = 1 / area;
  for (let y = minY; y <= maxY; y++) {
    const py = y + 0.5;
    for (let x = minX; x <= maxX; x++) {
      const px = x + 0.5;
      const w0 = ((b.sx - px) * (c.sy - py) - (b.sy - py) * (c.sx - px)) * inv;
      const w1 = ((c.sx - px) * (a.sy - py) - (c.sy - py) * (a.sx - px)) * inv;
      const w2 = 1 - w0 - w1;
      if (w0 < 0 || w1 < 0 || w2 < 0) continue;
      const z = w0 * a.z + w1 * b.z + w2 * c.z;
      const pi = y * w + x;
      if (z > zbuf[pi]!) continue;
      zbuf[pi] = z;
      let r = w0 * a.r + w1 * b.r + w2 * c.r;
      let g = w0 * a.g + w1 * b.g + w2 * c.g;
      let bl = w0 * a.b + w1 * b.b + w2 * c.b;
      if (grain > 0) {
        const nse = hash2(x, y) * 2 - 1;
        const k = 1 + nse * grain;
        r *= k;
        g *= k;
        bl *= k;
      }
      const o = pi * 4;
      data[o] = Math.max(0, Math.min(255, (r * 255) | 0));
      data[o + 1] = Math.max(0, Math.min(255, (g * 255) | 0));
      data[o + 2] = Math.max(0, Math.min(255, (bl * 255) | 0));
      data[o + 3] = 255;
    }
  }
}
